import mapboxgl, { LngLatBoundsLike } from "mapbox-gl";
import type { Envelope } from "stores/map/mapTypes";
import { MapInteraction } from "types";

import { setPanToNetworkBoundsActive } from "./mapState";

export type { Envelope } from "stores/map/mapTypes";

export const panToNetworkBounds = async (
  wellId: string,
  facilityIds: string[],
  map: mapboxgl.Map,
  envelope: Envelope | undefined | null
) => {
  if (
    envelope &&
    envelope.minY !== 0 &&
    envelope.minX !== 0 &&
    envelope.maxX !== 0 &&
    envelope.maxY !== 0 &&
    envelope.minX !== envelope.maxX &&
    envelope.minY !== envelope.maxY
  ) {
    // Set flag to prevent other zoom mechanisms while this one is active
    setPanToNetworkBoundsActive(true);

    const bounds = [
      [envelope.minX, envelope.minY],
      [envelope.maxX, envelope.maxY]
    ] as LngLatBoundsLike;

    await new Promise<void>((resolve) => {
      map.once(MapInteraction.MoveEnd, () => {
        map.once(MapInteraction.Idle, () => {
          // Reset the flag after zoom animation completes
          setPanToNetworkBoundsActive(false);
          resolve();
        });
      });

      map.fitBounds(bounds, {
        padding: 50,
        duration: 1200
      });
    });
  }
};

// Utility function to combine multiple envelopes into one larger envelope
export const combineEnvelopes = (envelopes: Envelope[]): Envelope | null => {
  const validEnvelopes = envelopes.filter(
    (envelope) =>
      envelope &&
      envelope.minY !== 0 &&
      envelope.minX !== 0 &&
      envelope.maxX !== 0 &&
      envelope.maxY !== 0 &&
      envelope.minX !== envelope.maxX &&
      envelope.minY !== envelope.maxY
  );

  if (validEnvelopes.length === 0) {
    return null;
  }

  return validEnvelopes.reduce((combined, envelope) => {
    return {
      minX: Math.min(combined.minX, envelope.minX),
      minY: Math.min(combined.minY, envelope.minY),
      maxX: Math.max(combined.maxX, envelope.maxX),
      maxY: Math.max(combined.maxY, envelope.maxY)
    };
  });
};

// Function to zoom to combined bounds from multiple envelopes
export const panToCombinedNetworkBounds = async (
  map: mapboxgl.Map,
  envelopes: Envelope[],
  options?: { padding?: number; duration?: number }
) => {
  const combinedEnvelope = combineEnvelopes(envelopes);

  if (!combinedEnvelope) {
    return;
  }

  const bounds = [
    [combinedEnvelope.minX, combinedEnvelope.minY],
    [combinedEnvelope.maxX, combinedEnvelope.maxY]
  ] as LngLatBoundsLike;

  await new Promise<void>((resolve) => {
    map.once(MapInteraction.MoveEnd, () => {
      map.once(MapInteraction.Idle, () => resolve());
    });
    map.fitBounds(bounds, {
      padding: options?.padding ?? 50,
      duration: options?.duration ?? 1200
    });
  });
};
